import React, { useCallback, useEffect, useState } from 'react';
import { calendarApi } from './api';
import { CHANGED_EVENT } from './calendarToday';
import { followupHref, whenLabel, openFormInAcord } from './calendarFollowup';

// =============================================================================
// 5.38.0 the client's Follow-ups panel + log (client main folder). Upcoming (soonest first) and Past. Rows come from the
// permanent InsureDocs record (cal_followups), refreshed from the calendar cache by UID on every load — a time moved on
// an iPhone shows here, a delete on a phone stays as "Deleted", and the log says "changed on calendar" with the time
// InsureDocs noticed. Visible to anyone who can open the client. Clicking a row opens the item in the Calendar tab for
// people allowed to edit that calendar (the owner, admins); everyone else gets the read-only details + log.
// =============================================================================

const WINDOW = 'insuredocs-calendar';
const STATUS_COLOR = { Scheduled: '#2563eb', 'Done (past)': '#64748b', 'Task completed': '#16a34a', Deleted: '#b91c1c' };

function fmtStamp(s) {
  return s ? whenLabel(s) : '';
}

function Row({ r, me, C, onOpen, open }) {
  const [expanded, setExpanded] = useState(false);
  const snippet = (r.note || '').replace(/\s+/g, ' ').trim();
  const click = () => {
    if (r.can_edit && !r.deleted) onOpen(r);
    else setExpanded((x) => !x);
  };
  return (
    <div data-testid="followup-row" data-status={r.status} data-kind={r.kind} style={{ borderBottom: `1px solid ${C.border}` }}>
      <div onClick={click} title={r.can_edit && !r.deleted ? 'Open in the calendar' : 'Show details'}
        style={{ display: 'grid', gridTemplateColumns: '170px 92px 130px minmax(0,1fr) 150px 120px', gap: 8, alignItems: 'center', padding: '6px 8px', fontSize: 12, cursor: 'pointer' }}>
        <span data-testid="followup-when">{r.kind === 'task' ? (r.start ? `Due ${fmtStamp(r.start)}` : 'No due date') : fmtStamp(r.start)}</span>
        <span>{r.kind === 'task' ? '✔ Task' : '📅 Appointment'}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, background: r.color || '#3b82f6', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.owner ? r.owner.full_name : '—'}</span>
        </span>
        <span style={{ overflow: 'hidden', minWidth: 0 }}>
          {r.form_label && (    /* 5.42.0 made from an ACORD form: "📝 ACORD: …" / "📝 Carrier: …" / "📝 Custom: …" */
            <span data-testid="followup-form-label" title={r.form_deleted ? 'This form was deleted in ACORD' : 'Open this form in ACORD'}
              onClick={(e) => { e.stopPropagation(); if (!r.form_deleted) openFormInAcord(r.form_type, r.form_id); }}
              style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0f766e', fontWeight: 600, cursor: r.form_deleted ? 'default' : 'pointer' }}>
              📝 {r.form_label}{r.form_deleted && <span style={{ marginLeft: 6, padding: '0 5px', borderRadius: 8, fontSize: 10, color: '#fff', background: '#64748b' }} data-testid="followup-form-deleted">form deleted</span>}
            </span>
          )}
          <span style={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: r.deleted ? 'line-through' : 'none' }} data-testid="followup-row-title">{r.title}</span>
          {snippet && <span style={{ color: C.dim, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{snippet}</span>}
        </span>
        <span style={{ color: C.dim }}>{r.created_by || '—'}<br />{fmtStamp(r.created_at)}</span>
        <span>
          <span data-testid="followup-status" style={{ padding: '1px 6px', borderRadius: 9, fontSize: 11, fontWeight: 700, color: '#fff', background: STATUS_COLOR[r.status] || '#64748b' }}>{r.status}</span>
          {r.changed && <span title="Changed on a calendar outside InsureDocs" style={{ marginLeft: 4, padding: '1px 6px', borderRadius: 9, fontSize: 11, fontWeight: 700, color: '#fff', background: '#d97706' }} data-testid="followup-changed">Changed</span>}
          <span onClick={(e) => { e.stopPropagation(); setExpanded((x) => !x); }} style={{ marginLeft: 6, color: C.dim, cursor: 'pointer' }} title="Log" data-testid="followup-log-toggle">{expanded ? '▾' : '▸'}</span>
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '4px 12px 10px 24px', fontSize: 12, color: C.text }} data-testid="followup-details">
          {r.note && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}>{r.note}</div>}
          {r.can_edit && !r.deleted && <button type="button" onClick={() => onOpen(r)} style={{ marginBottom: 6, padding: '3px 10px', fontSize: 11, border: 'none', borderRadius: 5, background: '#7c3aed', color: '#fff', cursor: 'pointer' }}>Open in calendar</button>}
          {r.form_label && !r.form_deleted && <button type="button" onClick={() => openFormInAcord(r.form_type, r.form_id)} data-testid="followup-open-form" style={{ marginBottom: 6, marginLeft: 6, padding: '3px 10px', fontSize: 11, border: 'none', borderRadius: 5, background: '#0f766e', color: '#fff', cursor: 'pointer' }}>📝 Open form</button>}
          {!r.can_edit && !r.deleted && <div style={{ color: C.dim, marginBottom: 6 }}>Only {r.owner ? r.owner.full_name : 'the owner'} or an admin can open this item.</div>}
          <div style={{ fontWeight: 700, fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Log</div>
          {r.log.map((l, i) => (
            <div key={i} data-testid="followup-log" style={{ display: 'flex', gap: 8 }}>
              <span style={{ color: C.dim, minWidth: 170 }}>{fmtStamp(l.at)}</span>
              <span style={{ fontWeight: 600 }}>{l.action}</span>
              <span style={{ color: C.dim }}>{l.by ? `by ${l.by}` : ''}{l.detail ? ` — ${l.detail}` : ''}</span>
            </div>
          ))}
        </div>
      )}
      {open}
    </div>
  );
}

export default function ClientFollowups({ client, me, reloadKey, theme }) {
  const t = theme || {};
  const C = { panel: t.panelBg || '#fff', border: t.borderColor || '#ddd', text: t.textPrimary || '#222', dim: t.textSecondary || '#666' };
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(() => {
    calendarApi.followups(client.id).then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not load follow-ups'));
  }, [client.id]);
  useEffect(() => { setData(null); load(); }, [load, reloadKey]);
  useEffect(() => {
    const on = () => load();
    window.addEventListener(CHANGED_EVENT, on);
    window.addEventListener('focus', on);
    return () => { window.removeEventListener(CHANGED_EVENT, on); window.removeEventListener('focus', on); };
  }, [load]);

  const openRow = (r) => { window.open(followupHref(r, me), WINDOW); };
  const up = data ? data.upcoming : [];
  const past = data ? data.past : [];
  if (data && up.length === 0 && past.length === 0) {
    return <div data-testid="client-followups" data-empty="1" style={{ margin: '0 8px 6px', fontSize: 12, color: C.dim }}>📅 No follow-ups for this client yet.</div>;
  }
  return (
    <div data-testid="client-followups" style={{ margin: '0 8px 8px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.panel, color: C.text }}>
      <div onClick={() => setCollapsed((x) => !x)} style={{ padding: '6px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>{collapsed ? '▸' : '▾'}</span>
        <span style={{ color: '#7c3aed' }}>📅 Follow-ups</span>
        <span style={{ fontWeight: 400, color: C.dim, fontSize: 12 }}>{data ? `${up.length} upcoming · ${past.length} past` : 'Loading…'}</span>
      </div>
      {err && <div style={{ padding: '4px 10px', color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      {!collapsed && data && (
        <div>
          <div style={{ padding: '2px 10px', fontSize: 11, fontWeight: 700, color: C.dim, textTransform: 'uppercase' }}>Upcoming</div>
          <div data-testid="followups-upcoming">
            {up.length === 0 && <div style={{ padding: '4px 10px', fontSize: 12, color: C.dim }}>Nothing upcoming.</div>}
            {up.map((r) => <Row key={r.id} r={r} me={me} C={C} onOpen={openRow} />)}
          </div>
          {past.length > 0 && (
            <>
              <div onClick={() => setShowPast((x) => !x)} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 700, color: C.dim, textTransform: 'uppercase', cursor: 'pointer' }} data-testid="followups-past-toggle">
                {showPast ? '▾' : '▸'} Past · {past.length}
              </div>
              {showPast && <div data-testid="followups-past">{past.map((r) => <Row key={r.id} r={r} me={me} C={C} onOpen={openRow} />)}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
